import type { Asset } from './api';

// ---------------------------------------------------------------------------
// Dragging an asset from the library onto a track.
//
// The track says what it takes. A source is already typed — video, image,
// audio — and already knows whether it is a still, because the render plane
// decides that by probing the demuxer rather than trusting a file extension.
// A track that refuses the wrong kind AT THE MOMENT OF THE DROP is better than
// one that accepts it and fails two minutes later in a render.
//
// The rule is a pure function so the refusal text the creator reads is the
// same one the tests read.
// ---------------------------------------------------------------------------

/** The MIME type the tray writes and the tracks read. One string, one place. */
export const ASSET_MIME = 'application/x-rookery-asset';

export interface DragAsset {
  id: string;
  kind: Asset['kind'];
  name: string;
  /** A source made from an image carries no duration; that is how a still is told. */
  seconds: number | null;
  /**
   * Set when what is dragged is already a SOURCE — a camera take, an uploaded
   * tape — rather than a library asset. The drop cites it directly; there is
   * nothing to mint. `id` is then the source's id too, so the tracks' rule,
   * which reads only `kind`, is unchanged.
   */
  sourceId?: string;
}

/**
 * A-roll is the footage that speaks — the take's own sound and length; B-roll
 * is footage under the narration. Both are a property of a scene; the lane a
 * clip lands on decides which it is. Sound is the bed under the whole
 * programme; cues are sounds placed on a scene.
 */
export type Track = 'aroll' | 'broll' | 'sound' | 'cues';

export function accepts(track: Track, a: Pick<DragAsset, 'kind'>): { ok: true } | { ok: false; why: string } {
  if (track === 'aroll') {
    if (a.kind === 'video') return { ok: true };
    // A still has no sound to speak, and A-roll IS the sound: the lane would
    // make a silent scene as long as nothing. B-roll holds it over the voice.
    if (a.kind === 'image') return { ok: false, why: 'a still has no sound to speak — drop it on B-roll' };
    if (a.kind === 'audio') return { ok: false, why: 'audio cannot go on the A-roll track — drop it on Sound or Cues' };
    return { ok: false, why: 'a document cannot be cut into a scene' };
  }
  if (track === 'broll') {
    if (a.kind === 'video' || a.kind === 'image') return { ok: true };
    if (a.kind === 'audio') return { ok: false, why: 'audio cannot go on the b-roll track — drop it on Sound or Cues' };
    return { ok: false, why: 'a document cannot be cut into a scene' };
  }
  if (track === 'cues') {
    if (a.kind === 'audio') return { ok: true };
    if (a.kind === 'image') return { ok: false, why: 'a still cannot be a sound cue' };
    if (a.kind === 'video') return { ok: false, why: 'footage cannot be a sound cue — drop its audio, not the clip' };
    return { ok: false, why: 'a document has no sound' };
  }
  // sound
  if (a.kind === 'audio') return { ok: true };
  if (a.kind === 'image') return { ok: false, why: 'a still cannot go on the sound track' };
  if (a.kind === 'video') {
    // ffmpeg would happily take a clip's audio stream, and that is exactly why
    // it is refused: a bed that is silently the sound of a clip in the library
    // is a confusing video nobody asked for. The mix route refuses it too.
    return { ok: false, why: 'footage cannot be a music bed — drop its audio, not the clip' };
  }
  return { ok: false, why: 'a document has no sound' };
}

export function writeDrag(e: React.DragEvent, a: DragAsset): void {
  e.dataTransfer.setData(ASSET_MIME, JSON.stringify(a));
  e.dataTransfer.effectAllowed = 'copy';
}

export function readDrag(e: React.DragEvent): DragAsset | null {
  const raw = e.dataTransfer.getData(ASSET_MIME);
  if (!raw) return null;
  try {
    const a = JSON.parse(raw) as DragAsset;
    return a && typeof a.id === 'string' && typeof a.kind === 'string' ? a : null;
  } catch {
    return null;
  }
}

/** True while the thing being dragged is one of ours. Types are readable during dragover; data is not. */
export function isAssetDrag(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer.types).includes(ASSET_MIME);
}
